import { useEffect, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { BsCheckCircle } from "react-icons/bs";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import Text from "@CubeComponents/html/Text/Text";
import type { ReconciliationRow } from "./reconciliationHelper";
import { exportRowsToXlsx } from "./excelExportHelper";

interface ReconciliationLogModalProps {
  isVisible: boolean;
  onClose: () => void;
  emptyContainers: ReconciliationRow[];
  unmatchedFiles: ReconciliationRow[];
}

type TabKey = "empty" | "unmatched";

type GridRow = ReconciliationRow & { id: number };

interface TabConfig {
  key: TabKey;
  label: string;
  rows: ReconciliationRow[];
  emptyText: string;
  sheetName: string;
  filePrefix: string;
}

const gridSx = {
  "--DataGrid-rowBorderColor": "var(--stroke-primary)",
  border: "1px solid var(--stroke-primary)",
  color: "var(--text-primary)",
  fontFamily: '"Source Sans Pro", sans-serif',
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "var(--surface-subtle)",
    color: "var(--text-primary)",
    padding: "0.5rem 0.75rem",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "600 !important",
    fontSize: "0.875rem",
  },
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    color: "var(--text-primary)",
    fontSize: "0.875rem",
    padding: "0.5rem 0.75rem !important",
  },
};

const ReconciliationLogModal = ({
  isVisible,
  onClose,
  emptyContainers,
  unmatchedFiles,
}: ReconciliationLogModalProps) => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState<TabKey>(emptyContainers.length > 0 ? "empty" : "unmatched");

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const tabConfigs: TabConfig[] = [
    {
      key: "empty",
      label: t("Empty Containers"),
      rows: emptyContainers,
      emptyText: t("All containers have files"),
      sheetName: t("Empty Containers"),
      filePrefix: "Empty_Containers",
    },
    {
      key: "unmatched",
      label: t("Unmatched Files"),
      rows: unmatchedFiles,
      emptyText: t("All uploaded files matched a container"),
      sheetName: t("Unmatched Files"),
      filePrefix: "Unmatched_Files",
    },
  ];

  const visibleTabs = tabConfigs.filter((tab) => tab.rows.length > 0 || tab.key === activeKey);
  const activeTab = visibleTabs.find((tab) => tab.key === activeKey) ?? visibleTabs[0];

  const handleExport = (rows: ReconciliationRow[], sheetName: string, filePrefix: string) => {
    const headers = [t("File Name"), t("Location"), t("File Type")];
    const data = rows.map((row) => [row.fileName, row.location, row.fileType]);
    exportRowsToXlsx(headers, data, sheetName, filePrefix);
  };

  const columns: GridColDef<GridRow>[] = [
    { field: "id", headerName: "#", width: 56, sortable: false, filterable: false, cellClassName: "text-gray-500" },
    { field: "fileName", headerName: t("File Name"), flex: 2, minWidth: 240, sortable: false, filterable: false },
    { field: "location", headerName: t("Location"), flex: 2, minWidth: 240, sortable: false, filterable: false },
    {
      field: "fileType",
      headerName: t("File Type"),
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GridRow>) =>
        params.value ? (
          <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-[color:var(--light-blue-tag)] text-[color:var(--text-primary)]">
            {params.value}
          </span>
        ) : null,
    },
  ];

  const renderGrid = (rows: ReconciliationRow[], emptyText: string) => {
    if (!rows.length) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3 text-[color:var(--text-gray)]">
          <BsCheckCircle className="w-10 h-10 text-[color:var(--success-strong)]" />
          <Text className="text-base">{emptyText}</Text>
        </div>
      );
    }
    const gridRows: GridRow[] = rows.map((row, index) => ({ id: index + 1, ...row }));
    return (
      <DataGrid
        rows={gridRows}
        columns={columns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        rowHeight={52}
        sx={gridSx}
      />
    );
  };

  const footer = (
    <div className="flex justify-end w-full">
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
        <Text>{t("Close")}</Text>
      </ButtonComponent>
    </div>
  );

  const modalBody = (
    <div className="flex flex-col h-full w-full">
      <Box className="w-full items-center border-b border-b-button_light">
        <Tabs
          value={activeTab?.key ?? false}
          className="ps-5 pe-5"
          sx={{ "& .MuiTabs-flexContainer": { gap: "2.5rem" } }}
        >
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={
                <Box className="flex items-center gap-1">
                  <Text>{`${tab.label} (${tab.rows.length})`}</Text>
                </Box>
              }
              onClick={() => setActiveKey(tab.key)}
              data-testid={`reconTab-${tab.key}`}
              className={tab.key === activeKey ? "font-bold" : ""}
            />
          ))}
        </Tabs>
      </Box>

      {activeTab && (
        <>
          <div className="flex justify-end px-1 py-2">
            <ToolTipComponent content={t("Export to Excel")}>
              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                onClick={() => handleExport(activeTab.rows, activeTab.sheetName, activeTab.filePrefix)}
                disabled={!activeTab.rows.length}
              >
                <div className="flex gap-1 items-center">
                  <IconComponent name="ExcelExportIcon" width="1rem" height="1rem" />
                  <Text className="inline">{t("Export")}</Text>
                </div>
              </ButtonComponent>
            </ToolTipComponent>
          </div>
          <Box className="flex-1 min-h-0" sx={{ width: "100%" }}>
            {renderGrid(activeTab.rows, activeTab.emptyText)}
          </Box>
        </>
      )}
    </div>
  );

  return (
    <ModalStateDialogComponent
      id="reconciliation-log-modal"
      isVisible={isVisible}
      width="90%"
      height="95%"
      onCancel={onClose}
      headerTitle={
        <span className="text-2xl text-[color:var(--primary-text)] font-semibold">
          {t("Upload Reconciliation Log")}
        </span>
      }
      modelContent={modalBody}
      footerContent={footer}
    />
  );
};

export default ReconciliationLogModal;
